/** Reports/logs pages + components/reports/* (empty states, shared). */
export const reports = {
  title: "Reports",
  description:
    "Playback history, top content, device uptime, events, the audit trail, and broadcast activity across your fleet.",

  accessDenied: {
    title: "Manager access required",
    description: "Reports are available to managers, admins, and owners.",
  },

  tabs: {
    history: "History",
    topContent: "Top content",
    uptime: "Uptime",
    events: "Events",
    audit: "Audit trail",
    broadcasts: "Broadcasts",
  },

  // Filter-bar placeholders / "all" options.
  filters: {
    groupBy: "Group by",
    allDevices: "All devices",
    allLocations: "All locations",
    allChannels: "All channels",
    allEventTypes: "All event types",
    allActions: "All actions",
    allPeople: "All people",
    timeRange: "Time range",
  },

  timeRange: {
    last24Hours: "Last 24 hours",
    last7Days: "Last 7 days",
    last30Days: "Last 30 days",
    last90Days: "Last 90 days",
    allTime: "All time",
    custom: "Custom dates",
    fromLabel: "From",
    toLabel: "To",
  },

  groupBy: {
    byTrack: "By track",
    byPlaylist: "By playlist",
    byDevice: "By device",
    byLocation: "By location",
  },

  // Table column headers, shared across the report tabs.
  columns: {
    track: "Track",
    channel: "Channel",
    device: "Device",
    location: "Location",
    duration: "Duration",
    playedAt: "Played at",
    plays: "Plays",
    totalTime: "Total time",
    devices: "Devices",
    lastPlayed: "Last played",
    uptime: "Uptime",
    connected: "Connected",
    sessions: "Sessions",
    lastSeen: "Last seen",
    event: "Event",
    triggeredBy: "Triggered by",
    detail: "Detail",
    when: "When",
    title: "Title",
    startedBy: "Started by",
  },

  // First-column header on the Top content tab (varies by group-by).
  groupLabel: {
    track: "Track",
    playlist: "Playlist",
    device: "Device",
    location: "Location",
  },

  // Guided empty states (M9.4): each report tab gets a fitting title, a one-line
  // "what to do next", and — where a real next step exists — a primary action.
  // Read-only tabs (events) guide the operator to widen the range instead.
  emptyState: {
    history: {
      title: "No plays in this range",
      description:
        "Nothing played in the selected time range. Try a wider range, or check that your players are online.",
      action: "View fleet",
    },
    topContent: {
      title: "Nothing to rank yet",
      description:
        "Your most-played tracks and playlists appear here once your players start streaming.",
      action: "Manage schedules",
    },
    uptime: {
      title: "No devices yet",
      description: "Add a device to start tracking its connectivity and uptime.",
      action: "Add a device",
    },
    events: {
      title: "No events in this range",
      description:
        "Device heartbeats, connections, and commands appear here. Try a wider time range.",
    },
    audit: {
      title: "No changes in this range",
      description:
        "Every change someone makes to players, playlists, schedules and people is recorded here. Try a wider time range.",
    },
    broadcasts: {
      title: "No broadcasts yet",
      description:
        "Live announcements you send to your players will show up here.",
      action: "Open a Control page",
    },
  },

  error: {
    title: "Couldn't load this report",
    description:
      "Something went wrong while loading. Check your connection and try again.",
  },

  exportCsv: "Export CSV",

  history: {
    untitled: "Untitled",
    // The CSV export is capped server-side; say so rather than implying the
    // file is always the complete log.
    exportNote:
      "Export CSV downloads the log for the filters above, newest first, up to 50,000 rows. Past that the file ends with a truncation note: narrow the range and export again.",
    outcome: {
      completed: "Completed",
      skipped: "Skipped",
      failed: "Failed",
      interrupted: "Interrupted",
      playing: "Playing",
    },
  },

  audit: {
    // Rendered as "<person> <action> <entity>", e.g. "Sam Diaz updated device".
    system: "System",
    unknownPerson: "Removed user",
    columns: {
      who: "Who",
      what: "What",
      target: "Target",
      details: "Details",
      when: "When",
    },
    loadMore: "Load older",
    noDetails: "—",
  },

  broadcasts: {
    // Sentence with an inline link + styled span; kept as text runs so the
    // existing JSX structure (wouter Link, emphasis span) is preserved.
    hint: {
      lead: "To go live, open a device's",
      controlPage: "Control page",
      press: "and press",
      holdToTalk: "Hold to talk",
      trailing: "to speak through your location's players.",
    },
    status: {
      live: "Live",
      ended: "Ended",
    },
  },

  // Compact duration formats ("1h 2m", "45s", "3m 5s").
  duration: {
    hoursMinutes: "{h}h {m}m",
    seconds: "{s}s",
    minutesSeconds: "{m}m {s}s",
  },
} as const;
